// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Grid, IconButton, Typography } from '@mui/material'
import { FC } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import { Alert } from '../../common/Alert'
import { CustomController } from '../../common/CustomController'
// others
import { useUpdateUserGeneralInfoMutation, UserStatus } from '../../../../generated'
import { USER_UPDATE_SUCCESS } from '../../../constants'
import { DispatchAndState, UpdateNameInputProps } from '../../../interfaceTypes'
import { updateNameSchema } from '../../../validationSchema'
import { UserAction, UserState } from '../userReducer'

export const UpdateName: FC<DispatchAndState<UserState, UserAction>> = ({ state, refetch }) => {
  const { currentUser } = state || {}
  const { id, firstName, lastName, status } = currentUser || {}

  const updateNameMethods = useForm<UpdateNameInputProps>({
    mode: 'all',
    resolver: yupResolver(updateNameSchema),
    defaultValues: {
      id,
      firstName: firstName || '',
      lastName: lastName || ''
    }
  })

  const { handleSubmit: updateNameHandleSubmit, reset: updateNameReset, setValue } = updateNameMethods

  const [updateUserGeneralInfoMutation, { loading }] = useUpdateUserGeneralInfoMutation({
    onError(error) {
      const { graphQLErrors } = error
      if (graphQLErrors) {
        graphQLErrors.map(({ message }) => {
          Alert.error(message)
        })
      }
    },
    onCompleted(data) {
      if (data) {
        const { updateUserGeneralInfo } = data
        const { response, user } = updateUserGeneralInfo || {}
        if (response) {
          const { status: responseStatus } = response
          if (responseStatus === 200) {
            Alert.success(USER_UPDATE_SUCCESS)
            if (user) {
              setValue('firstName', user.firstName || '')
              setValue('lastName', user.lastName || '')
            }
            refetch?.()
          }
        }
      }
    }
  })

  const nameSubmitHandler: SubmitHandler<UpdateNameInputProps> = async (data: UpdateNameInputProps) => {
    await updateUserGeneralInfoMutation({
      variables: {
        user: {
          id: id || '',
          firstName: data.firstName,
          lastName: data.lastName
        }
      }
    })
  }

  return (
    <FormProvider {...updateNameMethods}>
      <form onSubmit={updateNameHandleSubmit(nameSubmitHandler)}>
        <Box pb={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box flex={1}>
              <Typography variant="h5" fontSize="14px">
                Name:
              </Typography>
            </Box>

            <IconButton type="submit" aria-label="Save" disabled={status !== UserStatus.Active || loading}>
              <SaveRoundedIcon color={status === UserStatus.Active ? 'primary' : 'disabled'} />
            </IconButton>
          </Box>

          <Grid container spacing={2} rowSpacing={0.5}>
            <Grid item md={6} sm={12}>
              <CustomController controllerName="firstName" controllerLabel="First Name" fieldType="text" />
            </Grid>

            <Grid item md={6} sm={12}>
              <CustomController controllerName="lastName" controllerLabel="Last Name" fieldType="text" />
            </Grid>
          </Grid>
        </Box>
      </form>
    </FormProvider>
  )
}
